import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AnimatePresence } from "framer-motion";
import ScrollToTop from "./components/ScrollToTop";
import Index from "./pages/Index";
import { lazy, Suspense, useState } from "react";

// Code-split: keep homepage eager (LCP route), lazy-load every other page
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const Auth = lazy(() => import("./pages/Auth"));
const NexusATS = lazy(() => import("./pages/NexusATS"));
const Elempleo = lazy(() => import("./pages/Elempleo"));
const PsychometricTests = lazy(() => import("./pages/PsychometricTests"));
const RecruitmentSelection = lazy(() => import("./pages/RecruitmentSelection"));
const SalarySurvey = lazy(() => import("./pages/SalarySurvey"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Ecosystem = lazy(() => import("./pages/Ecosystem"));
const RegisterCV = lazy(() => import("./pages/RegisterCV"));
const JobOffers = lazy(() => import("./pages/JobOffers"));
const NexusLab = lazy(() => import("./pages/NexusLab"));
const SalaryCalculator = lazy(() => import("./pages/SalaryCalculator"));
const Blog = lazy(() => import("./pages/Blog"));
const Orienta360 = lazy(() => import("./pages/Orienta360"));
const TalentConnect = lazy(() => import("./pages/TalentConnect"));
const Descargables = lazy(() => import("./pages/Descargables"));
const Candidatos = lazy(() => import("./pages/Candidatos"));
const ClimateSurvey = lazy(() => import("./pages/ClimateSurvey"));
const NexusLanguages = lazy(() => import("./pages/NexusLanguages"));
const LeadThankYou = lazy(() => import("./pages/LeadThankYou"));
const AGente = lazy(() => import("./pages/AGente"));
const ComoFunciona = lazy(() => import("./pages/ComoFunciona"));
const Contacto = lazy(() => import("./pages/Contacto"));
const CasosDeExito = lazy(() => import("./pages/CasosDeExito"));
const Industrias = lazy(() => import("./pages/Industrias"));
const EnvironmentalPolicy = lazy(() => import("./pages/EnvironmentalPolicy"));
const NonDiscriminationPolicy = lazy(() => import("./pages/NonDiscriminationPolicy"));
const SustainabilityReport = lazy(() => import("./pages/SustainabilityReport"));
const InicioSection = lazy(() => import("./pages/sections/InicioSection"));
const SolucionesSection = lazy(() => import("./pages/sections/SolucionesSection"));
const ResultadosSection = lazy(() => import("./pages/sections/ResultadosSection"));
const PorQueNexusSection = lazy(() => import("./pages/sections/PorQueNexusSection"));
const PartnersSectionPage = lazy(() => import("./pages/sections/PartnersSectionPage"));
const PreguntasFrecuentesSection = lazy(() => import("./pages/sections/PreguntasFrecuentesSection"));
const AgendarDemoSection = lazy(() => import("./pages/sections/AgendarDemoSection"));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
  </div>
);

const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<RouteFallback />}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/nexus-ats" element={<NexusATS />} />
          <Route path="/elempleo" element={<Elempleo />} />
          <Route path="/pruebas-psicometricas" element={<PsychometricTests />} />
          <Route path="/reclutamiento-seleccion" element={<RecruitmentSelection />} />
          <Route path="/encuesta-salarial" element={<SalarySurvey />} />
          <Route path="/sobre-nosotros" element={<AboutUs />} />
          <Route path="/ecosistema" element={<Ecosystem />} />
          <Route path="/registrar-cv" element={<RegisterCV />} />
          <Route path="/ofertas-empleo" element={<JobOffers />} />
          <Route path="/nexus-lab" element={<NexusLab />} />
          <Route path="/calculadora-salarial" element={<SalaryCalculator />} />
          <Route path="/orientacion-vocacional" element={<Orienta360 />} />
          <Route path="/talent-connect" element={<TalentConnect />} />
          <Route path="/descargables" element={<Descargables />} />
          <Route path="/candidatos" element={<Candidatos />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/encuesta-clima-cultura" element={<ClimateSurvey />} />
          <Route path="/nexus-languages" element={<NexusLanguages />} />
          <Route path="/politica-de-privacidad" element={<PrivacyPolicy />} />
          <Route path="/terminos-y-condiciones" element={<TermsConditions />} />
          <Route path="/politica-ambiental" element={<EnvironmentalPolicy />} />
          <Route path="/politica-no-discriminacion" element={<NonDiscriminationPolicy />} />
          <Route path="/reporte-sostenibilidad-2026" element={<SustainabilityReport />} />
          <Route path="/gracias" element={<LeadThankYou />} />
          <Route path="/agente" element={<AGente />} />
          <Route path="/como-funciona" element={<ComoFunciona />} />
          <Route path="/contacto" element={<Contacto />} />
          <Route path="/casos-de-exito" element={<CasosDeExito />} />
          <Route path="/industrias" element={<Industrias />} />
          <Route path="/inicio" element={<InicioSection />} />
          <Route path="/soluciones" element={<SolucionesSection />} />
          <Route path="/resultados" element={<ResultadosSection />} />
          <Route path="/por-que-nexus" element={<PorQueNexusSection />} />
          <Route path="/partners" element={<PartnersSectionPage />} />
          <Route path="/preguntas-frecuentes" element={<PreguntasFrecuentesSection />} />
          <Route path="/agendar-demo" element={<AgendarDemoSection />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

const App = () => {
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
      },
    },
  }));
  
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <AnimatedRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
};

export default App;
